import { useEffect, useState } from "react";
import { api } from "../context/AuthContext";
import { toast } from "sonner";
import { apiErrMsg } from "../utils/apiErr";
import { formatDateEntreeEnVigueur, CHEMINS_DOCUMENTS_JURIDIQUES } from "../utils/legalGate";

/**
 * LegalUpdateModal — blocking overlay shown after login when legal
 * documents have been updated and need re-acceptance.
 *
 * Props:
 *   needsAcceptance: string[]  — list of docs to accept ("cgu", "cgv", "mandate", "partnership")
 *   isAssociation: bool        — if true, show partnership contract link
 *   onAccepted: () => void     — callback after successful acceptance
 *   acceptEndpoint: string     — API endpoint ("/auth/accept-legal" or "/associations/auth/accept-legal")
 *   onDismiss: () => void      — (#2377) fourni UNIQUEMENT pendant la fenêtre de grâce, quand
 *                                l'utilisateur a ouvert la modale lui-même depuis le bandeau :
 *                                elle devient refermable (« Plus tard »). Absent = mur bloquant.
 *   effectiveAt: string        — (#2377) date ISO d'entrée en vigueur, affichée dans ce cas.
 */
export default function LegalUpdateModal({ needsAcceptance = [], isAssociation = false, onAccepted, acceptEndpoint = "/auth/accept-legal", onDismiss, onLogout, effectiveAt }) {
  const dateEffet = formatDateEntreeEnVigueur(effectiveAt);
  const volontaire = typeof onDismiss === "function";
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(null);

  // Audit P0 UX (2026-04-21) — lock body scroll while the modal is
  // open. Without this, the page behind scrolls when the modal
  // content overflows, breaking the "blocking overlay" expectation.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // Une association ne lit PAS le meme CGU qu'un utilisateur : /association/cgu
  // sert « Conditions Generales d'Utilisation — Espace association », un texte
  // distinct (AssoCGUContent). La modale renvoyait TOUT LE MONDE vers /cgu, donc
  // une association re-acceptait un texte qui n'est pas le sien, sous le meme
  // numero de version — alors que son ecran d'inscription, lui, pointe bien vers
  // /association/cgu. Le backend archive desormais l'empreinte du bon texte
  // (services/legal_texts.DOCUMENT_PAR_ACTEUR) ; cette ligne aligne l'ecran.
  const docLabels = {
    cgu: {
      label: isAssociation
        ? "Conditions Generales d'Utilisation — Espace association"
        : "Conditions Generales d'Utilisation (CGU)",
      // Chemins pris dans la SOURCE UNIQUE : c'est la MÊME table qui exempte
      // ces pages du mur (utils/legalGate). Deux listes divergeraient un jour,
      // et ce jour-là l'impasse « accepter pour lire » reviendrait en silence.
      path: isAssociation
        ? CHEMINS_DOCUMENTS_JURIDIQUES.cguAssociation
        : CHEMINS_DOCUMENTS_JURIDIQUES.cguUtilisateur,
    },
    cgv: { label: "Conditions Generales de Vente (CGV)", path: CHEMINS_DOCUMENTS_JURIDIQUES.cgv },
    mandate: { label: "Mandat de Collecte", path: CHEMINS_DOCUMENTS_JURIDIQUES.mandat },
    // LEGACY (lot #148, 23/09/2026) : le serveur n'émet PLUS "partnership" dans
    // `needs_acceptance` — le contrat a été intégré aux CGU association et sa
    // page ne sert plus qu'un texte archivé. L'entrée reste pour qu'un bundle
    // ancien face à un serveur ancien ne tombe pas sur un libellé vide ; elle
    // se retirera quand plus aucune version en circulation ne pourra la
    // recevoir. Ne pas s'en servir pour rouvrir le document.
    partnership: { label: "Contrat de Partenariat", path: CHEMINS_DOCUMENTS_JURIDIQUES.partenariat },
  };

  /**
   * Open a legal doc in a new tab.
   *
   * A plain react-router <Link target="_blank"> used to sit inside a
   * z-[9999] blocking overlay and the click was silently swallowed
   * in some browsers (prod report 2026-04-16). Doing the window.open
   * ourselves inside a user-initiated click handler is 100% reliable
   * and gives us a fallback when the browser blocks the popup.
   *
   * 23/09/2026 — CE FALLBACK SE DÉCLENCHAIT À CHAQUE CLIC, MÊME QUAND
   * L'ONGLET S'OUVRAIT. La cause n'est pas un bug de navigateur : c'est la
   * SPÉCIFICATION. `window.open()` avec `noopener` dans la chaîne de features
   * rend `null` PAR CONSTRUCTION — le but de `noopener` est justement de ne
   * rendre AUCUNE référence à la fenêtre ouverte (HTML Living Standard,
   * §window.open : « If tokenizedFeatures["noopener"] ... return null »,
   * https://html.spec.whatwg.org/multipage/nav-history-apis.html#dom-open-dev,
   * consulté le 23/09/2026). Le `if (!w)` lisait donc « popup bloquée » sur
   * chaque ouverture réussie, et l'association voyait un bandeau orange
   * « Ouverture bloquee par votre navigateur » démenti par l'onglet qui
   * venait de s'ouvrir sous ses yeux. Sur mobile, où l'onglet passe au
   * premier plan puis revient, c'est précisément l'impression décrite par
   * Rachel Madec (ABC MucO) : « une fenêtre s'ouvre et se referme aussitôt ».
   *
   * On retire donc `noopener` de la chaîne de features — seul moyen d'obtenir
   * une référence et donc de savoir si l'ouverture a réussi — et on coupe le
   * lien à la main juste après. `w.opener = null` est l'idiome antérieur à
   * `noopener` et produit la même protection ; la cible est de toute façon
   * une page de ce même site.
   */
  const openDoc = (path) => {
    const w = window.open(path, "_blank");
    if (w) {
      // Coupe la référence inverse (équivalent manuel de `noopener`) : la page
      // ouverte ne doit pas pouvoir piloter celle qui l'a ouverte.
      try { w.opener = null; } catch (_) { /* navigateur qui refuse l'écriture */ }
      setPopupBlocked(null);
      return;
    }
    // Popup réellement bloquée. Offer the inline viewer instead so the
    // user can still read the text before accepting.
    setPopupBlocked(path);
  };

  const handleAccept = async () => {
    if (!accepted) return;
    setLoading(true);
    try {
      const body = {
        accept_cgu: needsAcceptance.includes("cgu"),
        accept_cgv: needsAcceptance.includes("cgv"),
        accept_mandate: needsAcceptance.includes("mandate"),
        accept_partnership: needsAcceptance.includes("partnership"),
      };
      await api.post(acceptEndpoint, body);
      toast.success("Conditions acceptees");
      if (onAccepted) onAccepted();
    } catch (err) {
      // Ne plus avaler la cause : ce `catch` masquait aussi bien un 401 (modale
      // montée pour une association mais postée sur l'endpoint utilisateur) que
      // le 400 « Aucun document accepté ». L'utilisateur relançait alors en
      // boucle un geste qui ne pouvait pas aboutir.
      toast.error(apiErrMsg(err, "Erreur lors de l'acceptation. Veuillez réessayer."));
    } finally {
      setLoading(false);
    }
  };

  return (
    // Audit P0 UX (2026-04-21) — z-[99999] pour dépasser le
    // CookieConsent (z-[9999]), le canvas confetti (z-index:9999),
    // et tout widget tiers éventuel (chat support, pop-ups). Le
    // backdrop ``bg-black/70`` est volontairement plus sombre que
    // l'ancien ``/60`` pour un meilleur contraste et signal visuel
    // "ceci bloque tout le reste".
    <div
      className="fixed inset-0 z-[99999] bg-black/70 flex items-center justify-center p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-update-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 md:p-8 max-h-[90vh] overflow-y-auto relative">
        {/* SORTIE. On ne permet PAS de FERMER ce mur tant que les documents ne
            sont pas acceptés — c'est son contrat, et il ne change pas. Mais
            « ne pas pouvoir fermer » n'est pas « ne pas pouvoir partir » : le
            bouton « Me déconnecter » du bas laisse l'utilisateur qui refuse
            quitter sa session, sans rien accepter. Il ne lève pas le mur, il
            lui donne une porte.
            AVANT LE 23/09/2026, CE COMMENTAIRE DÉCRIVAIT CE BOUTON — QUI
            N'EXISTAIT PAS. Un commentaire qui promet une sortie absente est
            pire que le silence : il endort la prochaine lecture. */}
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 text-xl font-bold">!</div>
          <div>
            <h2 id="legal-update-title" className="text-xl font-bold text-slate-900">Mise à jour des conditions</h2>
            {/* `isAssociation` était une prop DÉCLARÉE ET JAMAIS LUE (audit du
                05/09/2026) : la modale parlait à un particulier même montée
                pour une association. Elle est désormais lue — l'acceptation
                engage la structure, pas la personne qui clique, et le mot
                « compte » est ambigu pour une asso qui a aussi des adhérents. */}
            <p className="text-sm text-slate-500">
              {volontaire
                ? `${isAssociation ? "Les conditions de votre association évoluent" : "Nos conditions évoluent"}${dateEffet ? ` le ${dateEffet}` : " prochainement"}. Vous pouvez les accepter dès maintenant ; jusqu'à cette date, rien ne change pour vous.`
                : isAssociation
                  ? "Votre association doit accepter les nouvelles conditions pour continuer à publier et à recevoir des dons."
                  : "Vous devez accepter les nouvelles conditions pour continuer."}
            </p>
          </div>
        </div>

        {/* Documents to accept */}
        <div className="space-y-3 mb-6">
          {needsAcceptance.map((doc) => {
            const info = docLabels[doc];
            if (!info) return null;
            return (
              <div
                key={doc}
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors border border-slate-200"
              >
                <svg className="w-5 h-5 text-[#1DA1F2] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm font-medium text-slate-700 flex-1">{info.label}</span>
                {/* Primary action: open in a new tab via window.open. */}
                <button
                  type="button"
                  onClick={() => openDoc(info.path)}
                  className="text-xs font-semibold text-[#1DA1F2] hover:text-blue-700 px-2 py-1 rounded"
                >
                  Ouvrir ↗
                </button>
                {/* Fallback: full SPA navigation that leaves the modal
                    behind. The acceptance state is server-side so the
                    user can come back and finish. */}
                <a
                  href={info.path}
                  rel="noopener noreferrer"
                  className="text-[11px] text-slate-400 hover:text-slate-600 underline"
                  title="Ouvrir dans cet onglet (vous pourrez revenir accepter ensuite)"
                >
                  lire
                </a>
              </div>
            );
          })}
          {popupBlocked && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs">
              <p className="font-semibold text-amber-900 mb-1">Ouverture bloquee par votre navigateur.</p>
              <p className="text-amber-800 mb-2">
                Cliquez sur <strong>lire</strong> pour ouvrir le document dans cet onglet,
                ou autorisez les popups sur gively.fr puis reessayez.
              </p>
              <a
                href={popupBlocked}
                rel="noopener noreferrer"
                className="text-[#1DA1F2] font-semibold underline"
              >
                Ouvrir le document maintenant →
              </a>
            </div>
          )}
        </div>

        {/* Acceptance checkbox */}
        <label className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-200 cursor-pointer mb-6">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 w-5 h-5 rounded border-slate-300 text-[#1DA1F2] focus:ring-[#1DA1F2]"
          />
          <span className="text-sm text-slate-700">
            J'ai lu et j'accepte les nouvelles conditions
            {needsAcceptance.length > 1 ? " ci-dessus" : ""}.
          </span>
        </label>

        {/* Submit */}
        <button
          onClick={handleAccept}
          disabled={!accepted || loading}
          className={`w-full py-3 rounded-xl font-semibold text-white transition-all ${
            accepted && !loading
              ? "bg-[#1DA1F2] hover:bg-[#1a8cd8] shadow-lg shadow-blue-200"
              : "bg-slate-300 cursor-not-allowed"
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Enregistrement...
            </span>
          ) : (
            "Accepter et continuer"
          )}
        </button>
        {volontaire && (
          <button
            type="button"
            onClick={onDismiss}
            data-testid="legal-update-later"
            className="w-full mt-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-700"
          >
            Plus tard
          </button>
        )}
        {/* Mode OBLIGATOIRE : « Plus tard » n'existe pas (le mur doit tenir).
            Sans cette porte, l'utilisateur qui refuse n'avait AUCUNE action
            possible — ni fermer, ni Échap, ni cliquer le fond, ni changer de
            compte. Sur iPhone, où il n'y a ni bouton système ni geste de
            retour, cela revenait à devoir tuer l'application. */}
        {!volontaire && typeof onLogout === "function" && (
          <button
            type="button"
            onClick={onLogout}
            data-testid="legal-update-logout"
            className="w-full mt-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 underline underline-offset-2"
          >
            Me déconnecter
          </button>
        )}
      </div>
    </div>
  );
}
